export class WidgetLeaseLinkButton extends HTMLElement {
  public label: string = "";
  public button: HTMLAnchorElement | null = null;
  private href: string = "";
  private target: string = "_self";
  private styleN: string = "1";
  private sizeN: string = "";
  private type: "static" | "dynamic" = "dynamic";
  private static readonly buttonClass = "leaselink-button";

  constructor(initialData?: Partial<WidgetLeaseLinkButton>) {
    super();

    if (initialData) {
      Object.assign(this, initialData);
    }
    Object.assign(this, this.dataset);

    const shadow = this.attachShadow({ mode: "open" });
    const button = this.createButton();
    const style = this.createStyle();

    this.init(() => {
      shadow.appendChild(style);
      shadow.appendChild(button);
      this.addEventListeners(button);
    });
  }

  // react to changes on data-* attributes (dataset)
  static get observedAttributes() {
    return [
      "data-style-n",
      "data-size-n",
      "data-label",
      "data-href",
      "data-target",
    ];
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ) {
    if (oldValue === newValue) return;

    switch (name) {
      case "data-style-n":
        this.styleN = newValue || "";
        this.updateStyleClass();
        break;
      case "data-size-n":
        this.sizeN = newValue || "";
        this.updateSizeClass();
        break;
      case "data-label":
        this.label = newValue || "";
        if (this.button) {
          this.updateButtonText(this.label);
        }
        break;
      case "data-href":
        this.href = newValue || "";
        if (this.button) this.button.href = this.href;
        break;
      case "data-target":
        this.target = newValue || "_self";
        if (this.button) this.button.target = this.target;
        break;
    }
  }

  init(cb?: () => void) {
    if (cb) {
      cb();
    }
  }

  public show() {
    if (this.button) {
      this.button.style.display = "";
    }
  }

  public hide() {
    if (this.button) {
      this.button.style.display = "none";
    }
  }

  public setDisabled(disabled: boolean) {
    if (!this.button) {
      return;
    }
    if (disabled) {
      this.button.classList.add(`${WidgetLeaseLinkButton.buttonClass}-disabled`);
    } else {
      this.button.classList.remove(`${WidgetLeaseLinkButton.buttonClass}-disabled`);
    }
  }

  public setLoading(loading: boolean) {
    if (!this.button) {
      return;
    }
    if (loading) {
      this.button.classList.add(`${WidgetLeaseLinkButton.buttonClass}-loading`);
    } else {
      this.button.classList.remove(`${WidgetLeaseLinkButton.buttonClass}-loading`);
    }
  }

  public updateButtonText(buttonText: string) {
    if (this.button) {
      this.button.innerHTML = `<span>${buttonText}</span>`;
    }
  }

  private createButton() {
    this.button = document.createElement("a");
    this.button.className = WidgetLeaseLinkButton.buttonClass;
    if (this.styleN) {
      this.button.classList.add(`${WidgetLeaseLinkButton.buttonClass}-style-${this.styleN}`);
    }
    if (this.sizeN) {
      this.button.classList.add(`${WidgetLeaseLinkButton.buttonClass}-size-${this.sizeN}`);
    }
    if (this.type) {
      this.button.classList.add(`${WidgetLeaseLinkButton.buttonClass}-type-${this.type}`);
    }
    if (this.href) {
      this.button.href = this.href;
    }

    if (this.target) {
      this.button.target = this.target;
    }

    this.button.innerHTML = `<span>${this.label || this.innerHTML}</span>`;

    return this.button;
  }

  private updateStyleClass() {
    if (!this.button) return;
    // remove existing style classes
    Array.from(this.button.classList)
      .filter((c) => c.startsWith(`${WidgetLeaseLinkButton.buttonClass}-style-`))
      .forEach((c) => this.button!.classList.remove(c));
    if (this.styleN) {
      this.button.classList.add(`${WidgetLeaseLinkButton.buttonClass}-style-${this.styleN}`);
    }
  }

  private updateSizeClass() {
    if (!this.button) return;
    // remove existing size classes
    Array.from(this.button.classList)
      .filter((c) => c.startsWith(`${WidgetLeaseLinkButton.buttonClass}-size-`))
      .forEach((c) => this.button!.classList.remove(c));
    if (this.sizeN) {
      this.button.classList.add(`${WidgetLeaseLinkButton.buttonClass}-size-${this.sizeN}`);
    }
  }

  private createStyle() {
    const style = document.createElement("style");
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@300..500&display=swap');
      .${WidgetLeaseLinkButton.buttonClass} {
          --svg-purple-arrow: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2013.43%2013.42%22%3E%3Cg%20%3E%3Cpath%20d%3D%22M1.5%2C6.71h8.7%22%20fill%3D%22none%22%20stroke%3D%22%23822A85%22%20stroke-linecap%3D%22round%22%20stroke-width%3D%223%22%20stroke-miterlimit%3D%2210%22%2F%3E%3Cpath%20d%3D%22M6.72%2C11.92l5.21-5.21L6.72%2C1.5%22%20fill%3D%22none%22%20stroke%3D%22%23822A85%22%20stroke-linecap%3D%22round%22%20stroke-width%3D%223%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E");
          --svg-white-arrow: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2013.43%2013.42%22%3E%3Cg%20%3E%3Cpath%20d%3D%22M1.5%2C6.71h8.7%22%20fill%3D%22none%22%20stroke%3D%22%23fff%22%20stroke-linecap%3D%22round%22%20stroke-width%3D%223%22%20stroke-miterlimit%3D%2210%22%2F%3E%3Cpath%20d%3D%22M6.72%2C11.92l5.21-5.21L6.72%2C1.5%22%20fill%3D%22none%22%20stroke%3D%22%23fff%22%20stroke-linecap%3D%22round%22%20stroke-width%3D%223%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E");
          --svg-loader: url("data:image/svg+xml,%3Csvg%20class%3D%22active%20spinner%22%20width%3D%22160%22%20height%3D%22160%22%20viewBox%3D%220%200%20160%20160%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20clip-path%3D%22url(%23clip0_339_22)%22%3E%3Cg%20filter%3D%22url(%23filter0_i_339_22)%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M160%2080.0005C160%20124.183%20124.183%20160.001%2080%20160.001C35.8172%20160.001%200%20124.183%200%2080.0005C0%2035.8177%2035.8172%200.00050354%2080%200.00050354C124.183%200.00050354%20160%2035.8177%20160%2080.0005ZM140%2080C140%20113.137%20113.137%20140%2080%20140C46.8629%20140%2020%20113.137%2020%2080C20%2046.8629%2046.8629%2020%2080%2020C113.137%2020%20140%2046.8629%20140%2080Z%22%20fill%3D%22%23DCDCDC%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3Cpath%20d%3D%22M23.5%2023.5005C23.5%2023.5005%2044.5%200.128195%2079.7955%200.000455158C116%20-0.130575%20136.5%2023.5005%20136.5%2023.5005L122.795%2037.7936C122.795%2037.7936%20107.5%2020.0005%2079.7955%2020.0005C53.5%2020.0005%2037.5%2037.5005%2037.5%2037.5005L23.5%2023.5005Z%22%20fill%3D%22url(%23paint0_linear_339_22)%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3Cdefs%3E%3Cfilter%20id%3D%22filter0_i_339_22%22%20x%3D%220%22%20y%3D%220.00050354%22%20width%3D%22160%22%20height%3D%22160%22%20filterUnits%3D%22userSpaceOnUse%22%20color-interpolation-filters%3D%22sRGB%22%3E%3CfeFlood%20flood-opacity%3D%220%22%20result%3D%22BackgroundImageFix%22%3E%3C%2FfeFlood%3E%3CfeBlend%20mode%3D%22normal%22%20in%3D%22SourceGraphic%22%20in2%3D%22BackgroundImageFix%22%20result%3D%22shape%22%3E%3C%2FfeBlend%3E%3CfeColorMatrix%20in%3D%22SourceAlpha%22%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%20127%200%22%20result%3D%22hardAlpha%22%3E%3C%2FfeColorMatrix%3E%3CfeOffset%3E%3C%2FfeOffset%3E%3CfeGaussianBlur%20stdDeviation%3D%222.5%22%3E%3C%2FfeGaussianBlur%3E%3CfeComposite%20in2%3D%22hardAlpha%22%20operator%3D%22arithmetic%22%20k2%3D%22-1%22%20k3%3D%221%22%3E%3C%2FfeComposite%3E%3CfeColorMatrix%20type%3D%22matrix%22%20values%3D%220%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200%200.25%200%22%3E%3C%2FfeColorMatrix%3E%3CfeBlend%20mode%3D%22normal%22%20in2%3D%22shape%22%20result%3D%22effect1_innerShadow_339_22%22%3E%3C%2FfeBlend%3E%3C%2Ffilter%3E%3ClinearGradient%20id%3D%22paint0_linear_339_22%22%20x1%3D%22134.5%22%20y1%3D%2222.5%22%20x2%3D%2224%22%20y2%3D%2223.5004%22%20gradientUnits%3D%22userSpaceOnUse%22%3E%3Cstop%20stop-color%3D%22%23822a85%22%3E%3C%2Fstop%3E%3Cstop%20offset%3D%221%22%20stop-color%3D%22%23822a85%22%3E%3C%2Fstop%3E%3C%2FlinearGradient%3E%3CclipPath%20id%3D%22clip0_339_22%22%3E%3Crect%20width%3D%22160%22%20height%3D%22160%22%20fill%3D%22white%22%3E%3C%2Frect%3E%3C%2FclipPath%3E%3C%2Fdefs%3E%3C%2Fsvg%3E");
          font-family: "Open Sans", "Arial", sans-serif;
          position: relative;
          display: inline-block;
          padding: 14px 24px;
          text-align: center;
          color: #fff;
          background: linear-gradient(180deg, #333 0%, #000 100%);
          transition: all 0.5s;
          text-transform: uppercase;
          font-size: 12px;
          line-height: 1;
          text-decoration: none;
          box-sizing: border-box;
          cursor: pointer;
          margin-bottom: 1em;
          margin-top: 1em;
      }
      .${WidgetLeaseLinkButton.buttonClass}>span{
        position:relative;
      }

      .${WidgetLeaseLinkButton.buttonClass}:hover::before {
        left: 0;
        width: calc(100% + 32px);
      }

      .${WidgetLeaseLinkButton.buttonClass}-fixed-width {
          width: 247px;
      }

      .${WidgetLeaseLinkButton.buttonClass}::before,
      .${WidgetLeaseLinkButton.buttonClass}::after {
          position: absolute;
          left: 100%;
          top: 0;
          bottom: 0;
          content: "";
          width: 32px;
          color: inherit;
          transition: all 0.5s;
          box-sizing: border-box;
      }

      .${WidgetLeaseLinkButton.buttonClass}::after {
          background-position: center center;
          background-repeat: no-repeat;
          background-size: 15px auto;
      }
      .${WidgetLeaseLinkButton.buttonClass}:hover::after {
        width: 15px;
        animation: arrow 0.5s infinite alternate;
      }
      @keyframes arrow {
        0% {
          width: 29px;
        }
        100% {
          width: 18px;
        }
      }

      .${WidgetLeaseLinkButton.buttonClass}-size-small {
          padding: 12px;
      }
      .${WidgetLeaseLinkButton.buttonClass}-size-small::before,
      .${WidgetLeaseLinkButton.buttonClass}-size-small::after {
          width: 29px;
      }
      .${WidgetLeaseLinkButton.buttonClass}-size-small:hover::before {
        width: calc(100% + 29px);
      }
      .${WidgetLeaseLinkButton.buttonClass}-size-small::after {
          background-size: 13px auto;
      }
      .${WidgetLeaseLinkButton.buttonClass}-size-small:hover::after {
        width: 13px;
      }

      .${WidgetLeaseLinkButton.buttonClass}-size-large {
          font-size: 14px;
          padding: 16px 28px;
      }

      .${WidgetLeaseLinkButton.buttonClass}-size-large::before,
      .${WidgetLeaseLinkButton.buttonClass}-size-large::after {
          width: 38px;
      }
      .${WidgetLeaseLinkButton.buttonClass}-size-large:hover::before {
        width: calc(100% + 38px);
      }
      .${WidgetLeaseLinkButton.buttonClass}-size-large::after {
          background-size: 18px auto;
      }
      .${WidgetLeaseLinkButton.buttonClass}-size-large:hover::after {
        width: 18px;
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-1 {
          color: #fff;
          background: linear-gradient(180deg, #333 0%, #000 100%);
      }
      .${WidgetLeaseLinkButton.buttonClass}-style-1:hover {
        color: #000;
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-1::before {
          background: #f0f0f0;
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-1::after {
          background-image: var(--svg-purple-arrow);
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-2 {
          color: #000;
          background: none;
          box-shadow: inset 0 0 0 1px #c1c1c1;
      }
      .${WidgetLeaseLinkButton.buttonClass}-style-2:hover {
        color: #fff;
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-2::before {
          background: linear-gradient(180deg, #b400bf 0%, #822a85 100%);
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-2::after {
          background-image: var(--svg-white-arrow);
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-3 {
          color: #fff;
          background: linear-gradient(180deg, #b400bf 0%, #3f1940 100%, #371738 100%);
      }
      .${WidgetLeaseLinkButton.buttonClass}-style-3:hover {
        color: #000;
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-3::before {
          background: #f0f0f0;
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-3::after {
          background-image: var(--svg-purple-arrow);
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-4 {
          color: #fff;
          background: linear-gradient(180deg, #333 0%, #000 100%);
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-4::before {
          background: linear-gradient(180deg, #b400bf 0%, #822a85 100%);
      }

      .${WidgetLeaseLinkButton.buttonClass}-style-4::after {
          background-image: var(--svg-white-arrow);
      }
      .${WidgetLeaseLinkButton.buttonClass}-disabled {
        pointer-events: none;
        opacity: 0.5;
      }
      .${WidgetLeaseLinkButton.buttonClass}-loading {
      	cursor: wait;
        pointer-events: none;
      }
      .${WidgetLeaseLinkButton.buttonClass}-loading span {
        opacity: 0;
      }
      .${WidgetLeaseLinkButton.buttonClass}-loading::before {
        width: 0;
      }
      .${WidgetLeaseLinkButton.buttonClass}-loading::after {
      	content: "";
        display: block;
        position: absolute;
        top: 50%;
        left: 10px;
        width: 30px;
        margin-top: -15px;
        height: 30px;
        background-image: var(--svg-loader);
        background-position: center;
        background-size: 30px;
        background-repeat: no-repeat;
        animation: rotate-loading 1s linear infinite;
        transform-origin: center;
      }
      @keyframes rotate-loading { 100% { transform: rotate(360deg); } }
    `;
    return style;
  }

  private addEventListeners(button: HTMLAnchorElement) {
    button.addEventListener("mouseenter", () => {
      button.classList.add("mouse-enter");
      button.classList.remove("mouse-leave");
    });

    button.addEventListener("mouseleave", () => {
      button.classList.add("mouse-leave");
      button.classList.remove("mouse-enter");
    });
  }
}

if (!(window as any).customElements?.get("widget-leaselink-button")) {
  (window as any).customElements?.define("widget-leaselink-button", WidgetLeaseLinkButton);
}
